import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from '@/lib/toast';
import api from '@/lib/api';
import type { Quote, SignaturePageBlockContent } from '@/types';
import { QuoteStatus, BlockType } from '@/types';
import { getPublicQuoteStrings, normalizePublicQuoteLocale } from '@/lib/publicQuoteLocale';
import { countSignedRecipients, readSignatureRecipients } from '@/lib/signatureRecipients';

const nl = () => getPublicQuoteStrings('nl');

async function computeDocumentHash(blocks: { type: string; content: unknown }[]): Promise<string> {
  const payload = JSON.stringify(blocks.map((b) => ({ type: b.type, content: b.content })));
  const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function usePublicQuote() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const [signStatus, setSignStatus] = useState<string | null>(null);
  const [signEmbedUrl, setSignEmbedUrl] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  const [respondDialog, setRespondDialog] = useState<'accepted' | 'declined' | null>(null);
  const [signerName, setSignerName] = useState('');
  const [message, setMessage] = useState('');
  const [responding, setResponding] = useState(false);
  const [signingRecipientId, setSigningRecipientId] = useState<string | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const tracked = useRef(false);
  const isPrintMode = searchParams.has('print');
  /**
   * Interne blik vanuit het offerteoverzicht ('Bekijken'). De pagina rendert
   * normaal, maar registreert geen view: 'x keer bekeken' moet een klantsignaal
   * blijven en niet meetellen wanneer sales zelf even kijkt.
   */
  const isInternalPreview = searchParams.has('preview');

  useEffect(() => {
    const fetchQuote = async () => {
      if (!slug) return;
      try {
        const pdfAccessToken = searchParams.get('pdfAccessToken');
        const query = isPrintMode && pdfAccessToken
          ? `?pdfAccessToken=${encodeURIComponent(pdfAccessToken)}`
          : '';
        const data = await api.getPublicQuote(`${slug}${query}`);
        setQuote(data);
        if (!tracked.current) {
          tracked.current = true;
          if (data.status !== QuoteStatus.DRAFT && !isInternalPreview) {
            api.trackPublicQuoteView(slug, 'view').catch(() => {});
          }
        }
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const errorText = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '';
        const code = (err as { code?: string }).code;
        const noResponse = !(err as { response?: unknown }).response;
        const s = nl();
        if (status === 410) {
          setError(/withdrawn|ingetrokken/i.test(errorText) ? s.hook.withdrawn : s.hook.expired);
        } else if (status === 401) {
          setPasswordRequired(true);
        } else if (noResponse && (code === 'ERR_NETWORK' || (err as Error)?.message === 'Network Error')) {
          setError(s.hook.loadFailed);
        } else {
          setError(s.hook.notFound);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [slug, isPrintMode, searchParams]);

  useEffect(() => {
    if (!quote?.id) return;
    api.getSignStatus(quote.id).then((res) => {
      if (res.active && res.status) {
        setSignStatus(res.status);
        if (res.embedUrl) setSignEmbedUrl(res.embedUrl);
      }
    }).catch(() => {});
  }, [quote?.id]);

  useEffect(() => {
    const loc = normalizePublicQuoteLocale(quote?.publicLocale);
    const s = getPublicQuoteStrings(loc);
    if (searchParams.get('signed') === 'true') {
      setSignStatus('signed');
      toast.success(s.hook.signedSuccess);
      searchParams.delete('signed');
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get('declined') === 'true') {
      setSignStatus('declined');
      toast.info(s.hook.declinedInfo);
      searchParams.delete('declined');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, quote?.publicLocale]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !password) return;
    setLoading(true);
    setPasswordError(false);
    try {
      const data = await api.getPublicQuote(`${slug}?password=${encodeURIComponent(password)}`);
      setQuote(data);
      setPasswordRequired(false);
      if (!tracked.current) {
        tracked.current = true;
        if (data.status !== QuoteStatus.DRAFT && !isInternalPreview) {
          api.trackPublicQuoteView(slug, 'view', password || undefined).catch(() => {});
        }
      }
    } catch {
      setPasswordError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureChange = useCallback((dataUrl: string | null) => {
    setSignatureData(dataUrl);
  }, []);

  const handleDownloadPDF = async () => {
    if (!quote || !slug) return;
    const s = getPublicQuoteStrings(normalizePublicQuoteLocale(quote.publicLocale));
    if (quote.status === QuoteStatus.DRAFT) {
      toast.error(s.hook.draftNoDownload);
      return;
    }
    setPdfDownloading(true);
    try {
      api.trackPublicQuoteView(slug, 'download', password || undefined).catch(() => {});
      await api.downloadPublicPDF(slug, `${quote.title}.pdf`, password || undefined);
    } catch (error) {
      const detail = error instanceof Error ? error.message : '';
      toast.error(detail ? `${s.hook.pdfFailed}: ${detail}` : s.hook.pdfFailed);
    } finally {
      setPdfDownloading(false);
    }
  };

  const scrollToSignatureArea = useCallback(() => {
    const el = document.querySelector('[data-signature-area]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleAcceptAfterSign = useCallback(async () => {
    if (!quote || !slug) return;
    if (quote.status === QuoteStatus.DRAFT) return;
    const s = getPublicQuoteStrings(normalizePublicQuoteLocale(quote.publicLocale));
    if (!signatureData) {
      toast.error(s.hook.signFirst);
      return;
    }
    setResponding(true);
    try {
      const documentHash = await computeDocumentHash(quote.blocks);
      const updated = await api.respondToQuote(slug, 'accepted', undefined, undefined, signatureData, documentHash, undefined, password || undefined);
      setQuote(updated);
      toast.success(s.hook.acceptedSuccess);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (err as { message?: string })?.message
        || s.hook.actionFailed;
      toast.error(msg);
    } finally {
      setResponding(false);
    }
  }, [quote, slug, signatureData, password]);

  /** Eén van meerdere tekenbevoegden tekent; de offerte wordt pas geaccepteerd als iedereen tekende. */
  const handleSignAsRecipient = useCallback(
    async (recipientId: string, signatureDataUrl: string) => {
      if (!quote || !slug) return;
      if (quote.status === QuoteStatus.DRAFT) return;
      const s = getPublicQuoteStrings(normalizePublicQuoteLocale(quote.publicLocale));
      setSigningRecipientId(recipientId);
      try {
        const documentHash = await computeDocumentHash(quote.blocks);
        const updated = await api.respondToQuote(
          slug,
          'accepted',
          undefined,
          undefined,
          signatureDataUrl,
          documentHash,
          recipientId,
          password || undefined,
        );
        setQuote(updated);

        const updatedRecipients = readSignatureRecipients(
          updated.blocks?.find((b) => b.type === BlockType.SIGNATURE_PAGE)?.content,
        );
        const everyoneSigned =
          updatedRecipients.length > 0 && countSignedRecipients(updatedRecipients) === updatedRecipients.length;
        if (everyoneSigned) {
          toast.success(s.hook.acceptedSuccess);
        } else {
          toast.success(s.hook.signedPartially);
        }
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          || (err as { message?: string })?.message
          || s.hook.actionFailed;
        toast.error(msg);
      } finally {
        setSigningRecipientId(null);
      }
    },
    [quote, slug, password],
  );

  const handleRespond = async () => {
    if (!quote || !slug || !respondDialog) return;
    if (quote.status === QuoteStatus.DRAFT) return;
    const s = getPublicQuoteStrings(normalizePublicQuoteLocale(quote.publicLocale));
    if (respondDialog === 'accepted' && hasSignaturePage && !signatureData) {
      toast.error(s.hook.signFirst);
      return;
    }
    setResponding(true);
    try {
      const documentHash = respondDialog === 'accepted' ? await computeDocumentHash(quote.blocks) : undefined;
      const updated = await api.respondToQuote(slug, respondDialog, signerName || undefined, message || undefined, respondDialog === 'accepted' ? signatureData || undefined : undefined, documentHash, undefined, password || undefined);
      setQuote(updated);
      setRespondDialog(null);
      if (respondDialog === 'accepted') {
        toast.success(s.hook.acceptedSuccess);
      } else {
        toast.info(s.hook.declinedToast);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (err as { message?: string })?.message
        || s.hook.actionFailed;
      toast.error(msg);
    } finally {
      setResponding(false);
    }
  };

  const isAccepted = quote?.status === QuoteStatus.ACCEPTED || signStatus === 'signed';
  const isDeclined = quote?.status === QuoteStatus.DECLINED;
  const isResponded = isAccepted || isDeclined;
  const hasActiveSignRequest = signStatus && ['created', 'sent', 'viewed'].includes(signStatus);
  const signaturePageContent = quote?.blocks?.find(b => b.type === BlockType.SIGNATURE_PAGE)?.content as
    | SignaturePageBlockContent
    | undefined;
  const hasSignaturePage = Boolean(signaturePageContent) || (quote?.blocks?.some(b => b.type === BlockType.SIGNATURE_PAGE) ?? false);
  const isDraftPublic = quote?.status === QuoteStatus.DRAFT;
  const savedSignature = isAccepted ? signaturePageContent?.recipientSignature ?? null : null;

  const recipients = readSignatureRecipients(signaturePageContent);
  const signedRecipientCount = countSignedRecipients(recipients);
  const pendingRecipientCount = Math.max(recipients.length - signedRecipientCount, 0);
  /** Meerdere tekenbevoegden waarvan er al iemand tekende, maar nog niet iedereen. */
  const awaitingOtherSigners = recipients.length > 1 && signedRecipientCount > 0 && pendingRecipientCount > 0;

  return {
    slug,
    quote,
    loading,
    error,
    isPrintMode,
    passwordRequired,
    password,
    setPassword,
    passwordError,
    handlePasswordSubmit,
    signStatus,
    signEmbedUrl,
    signatureData,
    respondDialog,
    setRespondDialog,
    signerName,
    setSignerName,
    message,
    setMessage,
    responding,
    isAccepted,
    isDeclined,
    isResponded,
    hasActiveSignRequest,
    hasSignaturePage,
    savedSignature,
    handleSignatureChange,
    handleDownloadPDF,
    pdfDownloading,
    scrollToSignatureArea,
    handleAcceptAfterSign,
    handleSignAsRecipient,
    signingRecipientId,
    pendingRecipientCount,
    awaitingOtherSigners,
    handleRespond,
    isDraftPublic,
  };
}
